import React, { useState, useRef, useEffect } from 'react';
import { Button, message, Modal, Space, Dropdown } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import { attachmentExtensionsApi } from '../../services/api/attachmentExtensionsApi';
import { ocrApi } from '../../services/api/ocrApi';

export type CapturePurpose = 'vin' | 'odometer' | 'condition' | 'finding' | 'dvi' | 'completed';

const PURPOSE_TO_SUBTYPE: Record<CapturePurpose, string> = {
  vin: 'vin_photo', odometer: 'odometer', condition: 'condition', finding: 'finding',
  dvi: 'dvi', completed: 'completed',
};
const LABEL_BY_PURPOSE: Record<CapturePurpose, string> = {
  vin: 'Scan VIN', odometer: 'Photo Odometer',
  condition: 'Add Photo', finding: 'Add Photo', dvi: 'Add Photo', completed: 'Add Photo',
};

export interface CameraCaptureFieldProps {
  purpose: CapturePurpose;
  entityType: string;
  entityId: number;
  onUploaded: (result: {
    attachment: any;
    ocr?: { value: string; confidence: number; units?: 'mi' | 'km' };
  }) => void;
  ocrEnabled?: boolean;
  buttonLabel?: string;
  /** Optional caption/notes stored on the uploaded attachment (attachments.notes). */
  notes?: string;
  visibility?: 'internal' | 'customer';
}

const LOW_CONFIDENCE_THRESHOLD = 0.85;

const CameraCaptureField: React.FC<CameraCaptureFieldProps> = ({
  purpose, entityType, entityId, onUploaded, ocrEnabled = false, buttonLabel, notes, visibility,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [lowConfidenceResult, setLowConfidenceResult] = useState<{ value: string; confidence: number } | null>(null);

  // Attach / release the live camera stream as the in-app camera modal opens/closes.
  useEffect(() => {
    if (cameraOpen && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraOpen]);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  // Release the camera if the component unmounts while the modal is open.
  useEffect(() => stopStream, []);

  /** Shared upload path for both a chosen file and an in-app camera capture. */
  const uploadFile = async (file: File) => {
    setBusy(true);
    try {
      const attachment = await attachmentExtensionsApi.uploadPhoto({
        file, entity_type: entityType, entity_id: entityId, sub_type: PURPOSE_TO_SUBTYPE[purpose], notes, visibility,
      });
      if (!ocrEnabled || (purpose !== 'vin' && purpose !== 'odometer')) {
        onUploaded({ attachment });
        return;
      }
      const ocr = purpose === 'vin'
        ? await ocrApi.extractVin(attachment.id)
        : await ocrApi.extractOdometer(attachment.id);
      if (ocr.confidence < LOW_CONFIDENCE_THRESHOLD) {
        setLowConfidenceResult({ value: ocr.value, confidence: ocr.confidence });
        onUploaded({ attachment, ocr: { value: '', confidence: ocr.confidence } });
        return;
      }
      onUploaded({ attachment, ocr: { value: ocr.value, confidence: ocr.confidence, units: (ocr as any).units } });
    } catch (err: any) {
      message.error(`Upload failed: ${err?.message ?? 'unknown error'}`);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
      if (libraryRef.current) libraryRef.current.value = '';
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  /**
   * "Take photo" router:
   *  - Touch devices (mobile/tablet): use the native file input with
   *    capture="environment" — the OS camera UI is the best experience there.
   *  - Desktop: open an in-app webcam modal via getUserMedia. If there is no
   *    camera (or the user denies it), fall back to the plain file chooser.
   */
  const handleTakePhoto = async () => {
    const isTouch = typeof window !== 'undefined'
      && window.matchMedia?.('(pointer: coarse)').matches;
    if (isTouch) {
      inputRef.current?.click();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      libraryRef.current?.click(); // no camera API → file chooser
      return;
    }
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      setCameraOpen(true);
    } catch {
      // No camera available, or permission denied → fall back to file chooser.
      libraryRef.current?.click();
    }
  };

  const closeCamera = () => {
    stopStream();
    setCameraOpen(false);
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob: Blob | null = await new Promise(resolve =>
      canvas.toBlob(b => resolve(b), 'image/jpeg', 0.92));
    if (!blob) {
      message.error('Could not capture photo');
      return;
    }
    const file = new File([blob], `photo-${entityId}.jpg`, { type: 'image/jpeg' });
    closeCamera();
    await uploadFile(file);
  };

  const acceptLowConfidence = (decision: 'accept' | 'reject') => {
    if (decision === 'accept' && lowConfidenceResult) {
      onUploaded({
        attachment: undefined as any,
        ocr: { value: lowConfidenceResult.value, confidence: lowConfidenceResult.confidence },
      });
    }
    setLowConfidenceResult(null);
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" capture="environment"
        data-testid="camera-input" style={{ display: 'none' }} onChange={handleChange} />
      <input ref={libraryRef} type="file" accept="image/*"
        data-testid="library-input" style={{ display: 'none' }} onChange={handleChange} />
      <Dropdown menu={{ items: [
        { key: 'cam', label: 'Take photo', onClick: handleTakePhoto },
        { key: 'lib', label: 'Choose from library', onClick: () => libraryRef.current?.click() },
      ] }} trigger={['click']}>
        <Button icon={<CameraOutlined />} loading={busy}>
          {buttonLabel ?? LABEL_BY_PURPOSE[purpose]}
        </Button>
      </Dropdown>

      <Modal
        open={cameraOpen}
        title="Take photo"
        onCancel={closeCamera}
        destroyOnHidden
        footer={<Space>
          <Button onClick={closeCamera}>Cancel</Button>
          <Button type="primary" icon={<CameraOutlined />} loading={busy} onClick={capturePhoto}>
            Capture
          </Button>
        </Space>}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: '100%', borderRadius: 8, background: '#000' }}
        />
      </Modal>

      <Modal
        open={!!lowConfidenceResult}
        title="Low confidence"
        onCancel={() => acceptLowConfidence('reject')}
        footer={<Space>
          <Button onClick={() => acceptLowConfidence('reject')}>Re-take photo</Button>
          <Button type="primary" onClick={() => acceptLowConfidence('accept')}>Use anyway</Button>
        </Space>}
      >
        <p>We read: <strong>{lowConfidenceResult?.value}</strong></p>
        <p>Confidence: {Math.round((lowConfidenceResult?.confidence ?? 0) * 100)}%</p>
        <p>Please confirm or re-take.</p>
      </Modal>
    </>
  );
};

export default CameraCaptureField;
